import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {usePermissions} from "tmwx-design-common";
import React, {useEffect, useMemo, useState} from "react";
import {Button, Grid, Header, styles, Surface, Table, Text, useTheme} from '../../tmwx-design-web/src';
import {useNavigate, useSearchParams} from "react-router-dom";
import {isEmpty} from "gui-common/appHelper";
import {RootState} from "gui-common/redux/store";
import HouseOutlinedIcon from '@mui/icons-material/HouseOutlined';
import ElectricMeterOutlinedIcon from '@mui/icons-material/ElectricMeterOutlined';
import GasMeterOutlinedIcon from '@mui/icons-material/GasMeterOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import SolarPowerOutlinedIcon from '@mui/icons-material/SolarPowerOutlined';
import HeatPumpOutlinedIcon from '@mui/icons-material/HeatPumpOutlined';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import VerticalAlignTopOutlinedIcon from '@mui/icons-material/VerticalAlignTopOutlined';
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import efeImage from '../../utils/images/efe.png';
import inverterImage from '../../utils/images/inverter.png';
import smartMeterImage from '../../utils/images/smart_meter.png';
import solarPanel from '../../utils/images/SolarPanel.png';
import houseIcon from '../../utils/images/HouseIcon.png';
import towerIcon from '../../utils/images/TowerIcon.png';
import controlDataIcon from '../../utils/images/controlData.png';

import {
    getControlCommands,
    getControlData,
    getEfeEvents,
    getEfeMetrics,
    getEnapKepEvents,
    getInverterDataGrid,
    getInverterRatedPower,
    getMeterDataGrid,
    getMeterInverterDetailsList,
    getPodDetails,
    getPodEnergyBalance
} from "gui-common/redux/action/efe/podDetailAction";
import {
    ControlCommands,
    ControlData,
    PodMetrics,
    EnapKepEvents,
    InverterDataGrid,
    MeterDataGrid,
    MeterInverterData,
    PodDeviceData,
    PodEnergyBalance,
    PodEvents,
    RatedPower
} from "gui-common/redux/data/podDetail";
import {ColumnDef} from "@tanstack/react-table";

const formatFixedValue = (
    value: unknown,
    fractionDigits: number,
    unit: string,
    keepUnitWhenInvalid = false
): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return keepUnitWhenInvalid ? `- ${unit}` : '-';
    }

    return `${value.toFixed(fractionDigits)} ${unit}`;
};

const MeasurementLocationDetails = () => {
    const {t} = useTranslation()
    const dispatch: any = useDispatch();
    const {hasRights} = usePermissions()
    const navigate = useNavigate();
    const {roundness, padding, theme} = useTheme()

    const [searchParams, setSearchParams] = useSearchParams();
    const isLoading = useSelector<RootState, boolean>(state => state.podDetail.isLoading);
    const metricsLoading = useSelector<RootState, boolean>(state => state.podDetail.metricsLoading);
    const podDetails = useSelector<RootState, PodDeviceData>(state => state.podDetail.podEfeData);
    const meterInverterDataList = useSelector<RootState, MeterInverterData[]>(state => state.podDetail.meterInverterDataList);
    const podEnergyBalance = useSelector<RootState, PodEnergyBalance>(state => state.podDetail.podEnergyBalance);
    const podMetrics = useSelector<RootState, PodMetrics>(state => state.podDetail.podMetrics);
    const meterDataGrid = useSelector<RootState, MeterDataGrid>(state => state.podDetail.meterDataGrid);
    const controlCommandsList = useSelector<RootState, ControlCommands[]>(state => state.podDetail.controlCommandsList);
    const podEventsList = useSelector<RootState, PodEvents[]>(state => state.podDetail.podEventsList);
    const enapKepEventsList = useSelector<RootState, EnapKepEvents[]>(state => state.podDetail.enapKepEventsList);
    const controlData = useSelector<RootState, ControlData>(state => state.podDetail.controlData);
    const ratedPower = useSelector<RootState, RatedPower>(state => state.podDetail.ratedPower);
    const [id, setId] = useState<string>("");
    const [podId, setPodId] = useState<string>("");
    const meterDevice = useMemo(
        () => meterInverterDataList?.find((d: any) => d?.dteDeviceTypeTag === 'METER'),
        [meterInverterDataList]
    );
    const inverterDevices = useMemo(
        () => meterInverterDataList?.filter((d: any) => d?.dteDeviceTypeTag === 'INVERTER') ?? [],
        [meterInverterDataList]
    );
    const [inverterDataById, setInverterDataById] = useState<Record<string, InverterDataGrid>>({});

    const extractInverterGrid = (result: any): InverterDataGrid | null => {
        if (!result) return null;

        if (result.payload) {
            return extractInverterGrid(result.payload);
        }

        if (result.data) {
            return extractInverterGrid(result.data);
        }

        if (
            result.dsupwAvg != null ||
            result.dsupwAvgPhsa != null ||
            result.dphvAvg != null ||
            result.dhzAvg != null ||
            result.dwmaxlimpctLast != null
        ) {
            return result as InverterDataGrid;
        }

        return null;
    };


    useEffect(() => {
        const machineId = searchParams.get("id") as string;
        const podId = searchParams.get("podId") as string;
        if (!isEmpty(machineId) && hasRights(["efe_pod_details_view"])) {
            setId(machineId);
            dispatch(getPodDetails(+machineId));
            dispatch(getMeterInverterDetailsList(+machineId));
            if (!isEmpty(podId)){
                setPodId(podId);
                dispatch(getPodEnergyBalance(podId));
                dispatch(getEfeMetrics(podId));
                dispatch(getMeterDataGrid(podId));
                dispatch(getEnapKepEvents(podId));
                dispatch(getEfeEvents(podId));
                dispatch(getControlCommands(podId));
                dispatch(getInverterRatedPower(podId));
            }
        }
    }, [searchParams])

    useEffect(() => {
        if (podId && !isEmpty(podMetrics?.dctrlCmdIdLast)) {
            dispatch(getControlData(podId, podMetrics!.dctrlCmdIdLast));
        }
    }, [podId, podMetrics?.dctrlCmdIdLast]);

    // useEffect(() => {
    //     const inverterName = meterInverterDataList[inverterIdx]?.pdvDeviceInstanceName;
    //     const inverterNameEmul = meterInverterDataList[inverterIdx+1]?.pdvDeviceInstanceName;
    //
    //     if (!isEmpty(podId) && !isEmpty(inverterName)) {
    //         dispatch(getInverterDataGrid(podId, inverterName));
    //         if(!isEmpty(inverterNameEmul))
    //             dispatch(getInverterDataGridEmul(podId, inverterNameEmul));
    //     }
    // }, [dispatch, podId, inverterIdx, meterInverterDataList]);

    useEffect(() => {
        let isCancelled = false;

        const fetchAllInverterData = async () => {
            if (!podId || !inverterDevices.length) {
                if (!isCancelled) {
                    setInverterDataById({});
                }
                return;
            }

            const nextData: Record<string, InverterDataGrid> = {};

            for (const inverter of inverterDevices) {
                const inverterName = inverter?.pdvDeviceInstanceName;
                if (!inverterName) continue;

                try {
                    const result = await dispatch(getInverterDataGrid(podId, inverterName));
                    const grid = extractInverterGrid(result);

                    if (grid) {
                        nextData[inverterName] = grid;
                    } else {
                        console.warn('No inverter grid payload returned for', inverterName, result);
                    }
                } catch (error) {
                    console.error('Failed to load inverter grid for', inverterName, error);
                }
            }

            if (!isCancelled) {
                setInverterDataById(nextData);
            }
        };

        fetchAllInverterData();

        return () => {
            isCancelled = true;
        };
    }, [dispatch, podId, inverterDevices]);

    const columns = useMemo<ColumnDef<PodEvents>[]>(
        () => [
            {
                accessorKey: 'timestamp',
                meta: t('efe.details.timestamp'),
                maxSize: 20,
                header: () => <Text bold>{t('efe.details.timestamp')}</Text>,
            },
            {
                accessorKey: 'eventName',
                meta: t('efe.details.event'),
                maxSize: 40,
                header: () => <Text bold>{t('efe.details.event')}</Text>
            },
            {
                accessorKey: 'eventValue',
                meta: t('efe.details.eventValue'),
                header: () => <Text bold>{t('efe.details.eventValue')}</Text>
            }
        ],
        [dispatch, hasRights, padding, t]
    );

    return (
        <Surface style={{height: '100vh'}} elevation={3}>
            <Grid.Container maxWidth='xl' centered>
                <Header
                    title={t('efe.details.podData')}
                    rounded
                    actionLeft={<Button iconLeft={'arrow_back'} status='neutral' variant='ghost' onPress={() => {
                        navigate('/efe/pod/measurement-location')
                    }}/>}
                />

                <Surface style={{
                    ...styles.marginRight8, ...styles.marginLeft8, ...{
                        paddingTop: 0,
                        overflowX: 'auto'
                    }
                }} rounded>
                    <details open>
                        <summary style={{...styles.row, alignItems: 'center', padding: padding, backgroundColor: 'lightblue', cursor: 'pointer'}}>
                            <HouseOutlinedIcon/>
                            <Text bold style={styles.marginLeft8}>
                                {t('efe.details.pod')} | {podDetails?.podId ?? '-'} | {t('atr.logs.date')}: {
                                podMetrics?.timestamp
                                    ? (() => {
                                        const d = new Date(podMetrics.timestamp);
                                        const yyyy = d.getFullYear();
                                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                                        const dd = String(d.getDate()).padStart(2, '0');
                                        const hh = String(d.getHours()).padStart(2, '0');
                                        const mi = String(d.getMinutes()).padStart(2, '0');
                                        return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
                                    })()
                                    : '-'
                                }
                            </Text>
                        </summary>
                    <div style={{...styles.row, padding: padding, alignItems: 'center'}}>
                        <PinDropOutlinedIcon/>
                        <Text style={styles.marginLeft8}>{podDetails?.address ?? '-'}</Text>
                        <PersonOutlineOutlinedIcon style={styles.marginLeft8}/>
                        <Text style={styles.marginLeft8}>{podDetails?.customerName ?? '-'}</Text>
                    </div>

                    <div style={{...styles.row, padding: padding}}>
                        <img
                            src={towerIcon}
                            style={{height: '100px'}}
                        />

                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                            <Text bold
                                  style={{...styles.marginLeft8, ...styles.marginTop16}}>{t('efe.details.import')}</Text>

                            <Text bold
                                  style={{
                                      ...styles.marginLeft8,
                                      color:
                                          podEnergyBalance?.meterDmdwAvg > podEnergyBalance?.meterSupwAvg
                                              ? 'red'
                                              : 'green'
                                  }}>
                                {!meterDevice
                                    ? t('smallNoData')
                                    : podEnergyBalance
                                        ? Math.round(
                                            Math.abs(
                                                (podEnergyBalance.meterDmdwAvg ?? 0) -
                                                (podEnergyBalance.meterSupwAvg ?? 0)
                                            )
                                        ) + ' W'
                                        : t('smallNoData')}
                            </Text>

                            <Text
                                style={{
                                    ...styles.marginLeft8,
                                    fontSize: '70%',
                                    color:
                                        podEnergyBalance?.meterDmdwAvg > podEnergyBalance?.meterSupwAvg
                                            ? 'red'
                                            : 'green'
                                }}>
                                {
                                    !meterDevice
                                        ? `${">"}${">"} ${">"}${">"} ${">"}${">"} ${">"}${">"} ${">"}${">"}`
                                        : podEnergyBalance?.meterDmdwAvg > podEnergyBalance?.meterSupwAvg
                                            ? `${">"}${">"} ${">"}${">"} ${">"}${">"} ${">"}${">"} ${">"}${">"}`
                                            : `${"<"}${"<"} ${"<"}${"<"} ${"<"}${"<"} ${"<"}${"<"} ${"<"}${"<"}`
                                }
                            </Text>
                        </div>

                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <img
                                src={houseIcon}
                                style={{height: '100px'}}
                            />
                            <Text bold style={styles.marginLeft8}>{t('efe.details.consumption')}</Text>
                            <Text bold style={{...styles.marginLeft8, color: 'blue'}}>
                                {!meterDevice
                                    ? t('smallNoData')
                                    : podEnergyBalance
                                        ? Math.round(
                                            (podEnergyBalance.meterDmdwAvg ?? 0)
                                            - (podEnergyBalance.meterSupwAvg ?? 0)
                                            + (podEnergyBalance.inverterSupwAvg ?? 0)
                                        ) + ' W'
                                        : t('smallNoData')}
                            </Text>
                        </div>

                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                            <Text bold
                                  style={{...styles.marginLeft8, ...styles.marginTop16}}>{t('efe.details.production')}</Text>
                            <Text bold style={{
                                ...styles.marginLeft8,
                                color: podEnergyBalance?.inverterSupwAvg === 0 ? 'gray' : 'green'
                            }}>
                                {podEnergyBalance?.inverterSupwAvg === 0 ? '0 W' : Math.round(podEnergyBalance?.inverterSupwAvg) + ' W'}
                            </Text>
                            <Text
                                style={{
                                    ...styles.marginLeft8,
                                    fontSize: '70%',
                                    color: podEnergyBalance?.inverterSupwAvg === 0 ? 'gray' : 'green'
                                }}>{"<"}{"<"} {"<"}{"<"} {"<"}{"<"} {"<"}{"<"} {"<"}{"<"}</Text>
                        </div>

                        <img
                            src={solarPanel}
                            style={{height: '105px'}}
                        />
                    </div>

                    <div style={{
                        ...styles.row,
                        alignItems: 'center',
                        padding: padding,
                        border: '1px solid lightgray',
                        borderRadius: '8px',
                        width: 'fit-content',
                        height: 'fit-content'
                    }}>
                        <img
                            src={controlDataIcon}
                            style={{height: '50px'}}
                        />

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end'
                        }}>
                            <Text bold
                                  style={{fontSize: '120%', ...styles.marginLeft16}}>
                                {isEmpty(podMetrics?.dctrlCmdModeLast)
                                    ? t('efe.details.noControl')
                                    : podMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')
                                        ? t('efe.details.voltageLimit')
                                        : podMetrics?.dctrlCmdModeLast?.includes('EXPORT')
                                            ? t('efe.details.exportPowerLimit')
                                            : t('efe.details.inverterPowerLimit') + (podMetrics?.dctrlCmdModeLast?.includes('PERCENT') ? ' (%)' : ' (Watt)')}
                            </Text>
                            <Text bold style={{fontSize: '170%', ...styles.marginLeft8}}>
                                {controlData?.cocControlParam != null
                                    ? podMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')
                                        ? `${controlData.cocControlParam} V`
                                        : podMetrics?.dctrlCmdModeLast?.includes('PERCENT')
                                            ? `${controlData.cocControlParam} %`
                                            : `${controlData.cocControlParam} W`
                                    : '-'}
                            </Text>

                            <Text>
                                {t('efe.details.s')}: {controlData?.cocStartTime
                                    ? controlData.cocStartTime.slice(0, controlData.cocStartTime.lastIndexOf(':'))
                                    : '-'}
                            </Text>
                            <Text>
                                {t('efe.details.e')}: {controlData?.cocEndTime
                                    ? controlData.cocEndTime.slice(0, controlData.cocEndTime.lastIndexOf(':'))
                                    : '-'}
                            </Text>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(8, auto)',
                            padding: padding,
                            border: '1px solid lightgray',
                            borderRadius: '8px',
                            maxWidth: '950px',
                            columnGap: 8,
                            rowGap: 4,
                            alignItems: 'center',
                            justifyItems: 'center',
                            ...styles.marginTop8
                        }}
                    >
                        {/* Header row */}
                        <Text bold>{t('efe.variablesTable.source')}</Text>
                        <Text bold>{t('efe.id')}</Text>
                        <Text bold>{t('status')}</Text>
                        <Text bold>{t('efe.details.controlType')}</Text>
                        <Text bold style={{
                            whiteSpace: 'small',
                            maxWidth: 90
                        }}>{t('efe.details.controlParam')}</Text>
                        <Text bold>{t('efe.details.start')}</Text>
                        <Text bold>{t('efe.details.end')}</Text>
                        <Text bold>{t('efe.details.timestamp')}</Text>


                        {/* Data rows */}
                        {!controlCommandsList || controlCommandsList.length === 0 ? (
                            <div
                                style={{
                                    gridColumn: '1 / -1',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <Text>{t('noData')}</Text>
                            </div>
                        ) : (Array.from({length: 10}).map((_, i) => {
                                const item = controlCommandsList?.[i];

                                if (item == null) return null;

                                return (
                                    <React.Fragment key={i}>
                                        <Text>
                                            {item?.cocSource === 'K'
                                                ? 'KEP'
                                                : item?.cocSource === 'E'
                                                    ? 'ENAP'
                                                    : item?.cocSource ?? '-'}
                                        </Text>

                                        <Text
                                            style={{
                                                wordBreak: 'break-all',
                                                whiteSpace: 'small',
                                                maxWidth: 220
                                            }}
                                        >
                                            {item?.cocCommandId ?? '-'}
                                        </Text>

                                        <Text>{item?.cocStatus ?? '-'}</Text>

                                        <Text>{item?.cocControlType ?? '-'}</Text>

                                        <Text
                                            style={{
                                                whiteSpace: 'small',
                                                maxWidth: 50
                                            }}
                                        >
                                            {item?.cocControlParam != null
                                                ? item?.cocControlType?.includes('PERCENT')
                                                    ? `${item.cocControlParam} %`
                                                    : item?.cocControlType?.includes('VOLTAGE')
                                                        ? `${item.cocControlParam} V`
                                                        : `${item.cocControlParam} W`
                                                : '-'}
                                        </Text>

                                        <Text
                                            style={{
                                                wordBreak: 'break-all',
                                                whiteSpace: 'small',
                                                maxWidth: 90
                                            }}
                                        >
                                            {item?.cocStartTime
                                                ? item.cocStartTime.slice(
                                                    0,
                                                    item.cocStartTime.lastIndexOf(':')
                                                )
                                                : '-'}
                                        </Text>

                                        <Text
                                            style={{
                                                wordBreak: 'break-all',
                                                whiteSpace: 'small',
                                                maxWidth: 90
                                            }}
                                        >
                                            {item?.cocEndTime
                                                ? item.cocEndTime.slice(
                                                    0,
                                                    item.cocEndTime.lastIndexOf(':')
                                                )
                                                : '-'}
                                        </Text>

                                        <Text
                                            style={{
                                                wordBreak: 'break-all',
                                                whiteSpace: 'small',
                                                maxWidth: 90
                                            }}
                                        >
                                            {item?.cocCreationTime
                                                ? item.cocCreationTime.slice(
                                                    0,
                                                    item.cocCreationTime.lastIndexOf(':')
                                                )
                                                : '-'}
                                        </Text>
                                    </React.Fragment>
                                );
                            }
                        ))}
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, auto)',
                            padding: padding,
                            border: '1px solid lightgray',
                            borderRadius: '8px',
                            maxWidth: '750px',
                            columnGap: 8,
                            rowGap: 4,
                            alignItems: 'center',
                            justifyItems: 'center',
                            ...styles.marginTop8
                        }}
                    >
                        {/* Header row */}
                        <Text bold>{t('efe.details.timestamp')}</Text>
                        <Text bold>{t('efe.details.event')}</Text>
                        <Text bold>{t('efe.details.commandId')}</Text>


                        {/* Data rows */}
                        {!enapKepEventsList || enapKepEventsList.length === 0 ? (
                            <div
                                style={{
                                    gridColumn: '1 / -1',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <Text>{t('noData')}</Text>
                            </div>
                        ) : (Array.from({length: 10}).map((_, i) => {
                                const item = enapKepEventsList?.[i];

                                if (item == null) return null;

                                return (
                                    <React.Fragment key={i}>

                                        <Text>
                                            {item?.timestamp
                                                ? item.timestamp
                                                : '-'}
                                        </Text>

                                        <Text>{item?.eventName ?? '-'}</Text>

                                        <Text>{item?.eventValue ?? '-'}</Text>
                                    </React.Fragment>
                                );
                            }
                        ))}
                    </div>

                    </details>
                </Surface>

                <Surface style={{
                    ...styles.marginRight8, ...styles.marginLeft8, ...{
                        paddingTop: 8,
                        overflowX: 'auto'
                    }
                }} rounded>
                    <details open>
                        <summary style={{...styles.row, alignItems: 'center', padding: padding, backgroundColor: 'lightblue', cursor: 'pointer'}}>
                            <GasMeterOutlinedIcon/>
                            <Text bold style={styles.marginLeft8}>{t('efe.efeName')} | {podDetails?.systemId ?? '-'}</Text>
                        </summary>

                    <div style={{...styles.row, ...styles.justifyContent_spaceBetween, padding: padding}}>
                        <div>
                            <div style={{...styles.row}}>
                                <div>
                                    <div style={{
                                        ...styles.row,
                                        alignItems: 'center',
                                        padding: padding,
                                        border: '1px solid lightgray',
                                        borderRadius: '8px',
                                        width: 'fit-content',
                                        height: 'fit-content'
                                    }}>
                                        <ThermostatOutlinedIcon sx={{fontSize: '48px'}}/>
                                        <Text bold style={{fontSize: '170%', ...styles.marginLeft8}}>
                                            {formatFixedValue(podMetrics?.dtemperatureLast, 1, '°C', true)}
                                        </Text>
                                    </div>
                                </div>
                                <div style={styles.marginLeft8}>
                                    <div style={{
                                        ...styles.row,
                                        alignItems: 'center',
                                        padding: padding,
                                        border: '1px solid lightgray',
                                        borderRadius: '8px',
                                        width: 'fit-content',
                                        height: 'fit-content'
                                    }}>
                                        <WaterDropOutlinedIcon sx={{fontSize: '48px'}}/>
                                        <Text bold style={{fontSize: '170%', ...styles.marginLeft8}}>
                                            {formatFixedValue(podMetrics?.dhumidityLast, 1, '%', true)}
                                        </Text>
                                    </div>
                                </div>
                            </div>

                            <div style={styles.marginTop8}>
                                <iframe
                                    src={`https://grafana.telemet.works/d-solo/ef09v1a2fpf5se/efe-hes?orgId=1&from=${Date.now() - 24 * 3600000}&to=${Date.now()}&timezone=browser&var-pod_id=${podId}&theme=${theme}&panelId=1&__feature.dashboardSceneSolo`}
                                    width="450" height="200" frameBorder="2"></iframe>
                            </div>

                            <div style={{...styles.row, ...styles.marginTop8}}>
                                {[1, 2].map((relay) => {
                                    const value = relay === 1 ? podMetrics?.drelay1Last : podMetrics?.drelay2Last;

                                    return (
                                        <div key={relay} style={relay === 1 ? {} : styles.marginLeft16}>
                                            <div
                                                style={{
                                                    ...styles.row,
                                                    padding: padding,
                                                    border: '1px solid lightgray',
                                                    borderRadius: '8px',
                                                    width: 'fit-content',
                                                    height: 'fit-content'
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        border: '1px solid lightgray',
                                                        borderRadius: '8px',
                                                        width: 'fit-content',
                                                        height: 'fit-content'
                                                    }}
                                                >
                                                    <Text
                                                        style={{
                                                            fontSize: '120%',
                                                            ...styles.marginLeft8,
                                                            ...styles.marginRight8
                                                        }}
                                                    >
                                                        Relay
                                                    </Text>
                                                    <Text
                                                        style={{
                                                            fontSize: '120%',
                                                            ...styles.marginLeft8,
                                                            ...styles.marginTop8
                                                        }}
                                                    >
                                                        {relay}
                                                    </Text>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Text
                                                        bold
                                                        style={{
                                                            fontSize: '120%',
                                                            ...styles.marginLeft8
                                                        }}
                                                    >
                                                        {t('efe.details.manual')}
                                                    </Text>

                                                    <Text
                                                        bold
                                                        style={{
                                                            fontSize: '170%',
                                                            ...styles.marginLeft8
                                                        }}
                                                    >
                                                        {value === 1
                                                            ? t('efe.details.on')
                                                            : t('efe.details.off')}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{
                            ...styles.row,
                            padding: padding,
                            border: '1px solid lightgray',
                            borderRadius: '8px',
                            width: 'fit-content',
                            height: 'fit-content'
                        }}>
                            <div style={{...styles.justifyContent_center}}>
                                <Text bold>{t('efe.details.manufacturer')}:</Text>
                                <Text bold>{t('efe.details.type')}:</Text>
                                <Text bold>{t('efe.details.family')}:</Text>
                                <Text bold>{t('efe.details.hardwareVersion')}:</Text>
                                <Text bold>{t('efe.details.firmwareVersion')}:</Text>
                            </div>

                            <div style={styles.marginLeft8}>
                                <Text>{podDetails?.dmaName ?? '-'}</Text>
                                <Text>{podDetails?.dteTemplateName ?? '-'}</Text>
                                <Text>{podDetails?.pfaName ?? '-'}</Text>
                                <Text>{podDetails?.hwtVersion ?? '-'}</Text>
                                <Text>{podDetails?.activeFirmwareVersionName ?? '-'}</Text>
                            </div>

                            <img
                                src={
                                    podDetails?.dtePicture
                                        ? `data:image/png;base64,${podDetails?.dtePicture}`
                                        : efeImage
                                }
                                alt={t('efe.efeName')}
                                style={{marginLeft: 16, height: '100px'}}
                            />
                        </div>
                    </div>
                    </details>
                </Surface>

                <Surface style={{
                    ...styles.marginRight8, ...styles.marginLeft8, ...{
                        paddingTop: 8,
                        overflowX: 'auto'
                    }
                }} rounded>
                    <details open={!!meterDevice}>
                        <summary style={{...styles.row, alignItems: 'center', padding: padding, backgroundColor: 'lightblue', cursor: 'pointer'}}>
                            <ElectricMeterOutlinedIcon/>
                            <Text bold style={styles.marginLeft8}>
                                {`${t('efe.details.smartMeter')} | ${meterDevice ? (meterDevice?.pdvDeviceInstanceName ?? '-') : t('efe.details.noMeter')}`}
                            </Text>
                        </summary>
                    {meterDevice ? (
                    <div style={{...styles.row, ...styles.justifyContent_spaceBetween, padding: padding}}>
                        <div>
                            <div style={{
                                ...styles.row,
                                padding: padding,
                                border: '1px solid lightgray',
                                borderRadius: '8px',
                                width: 'fit-content'
                            }}>
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                    <Text bold>{t('efe.details.phase')}</Text>
                                    <Text>PhsA</Text>
                                    <Text>PhsB</Text>
                                    <Text>PhsC</Text>
                                </div>
                                {/* Vételezés (dmdw) */}
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', ...styles.marginLeft8}}>
                                    <Text bold>{t('efe.details.import')}</Text>
                                    {
                                        meterDataGrid?.ddmdwAvgPhsa != null &&
                                        meterDataGrid?.ddmdwAvgPhsb != null &&
                                        meterDataGrid?.ddmdwAvgPhsc != null
                                            ? <>
                                                <Text>{Math.round(meterDataGrid.ddmdwAvgPhsa)} W</Text>
                                                <Text>{Math.round(meterDataGrid.ddmdwAvgPhsb)} W</Text>
                                                <Text>{Math.round(meterDataGrid.ddmdwAvgPhsc)} W</Text>
                                            </>
                                            : <>
                                                <Text>-</Text>
                                                <Text>{meterDataGrid?.ddmdwAvg != null ? Math.round(meterDataGrid.ddmdwAvg) + ' W' : '-'}</Text>
                                                <Text>-</Text>
                                            </>
                                    }
                                </div>

                                {/* Visszatáplálás (supw) */}
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', ...styles.marginLeft8}}>
                                    <Text bold>{t('efe.details.export')}</Text>
                                    {
                                        meterDataGrid?.dsupwAvgPhsa != null &&
                                        meterDataGrid?.dsupwAvgPhsb != null &&
                                        meterDataGrid?.dsupwAvgPhsc != null
                                            ? <>
                                                <Text>{Math.round(meterDataGrid.dsupwAvgPhsa)} W</Text>
                                                <Text>{Math.round(meterDataGrid.dsupwAvgPhsb)} W</Text>
                                                <Text>{Math.round(meterDataGrid.dsupwAvgPhsc)} W</Text>
                                            </>
                                            : <>
                                                <Text>-</Text>
                                                <Text>{meterDataGrid?.dsupwAvg != null ? Math.round(meterDataGrid.dsupwAvg) + ' W' : '-'}</Text>
                                                <Text>-</Text>
                                            </>
                                    }
                                </div>

                                {/* Feszültség (phv) */}
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', ...styles.marginLeft8}}>
                                    <Text bold>{t('efe.details.voltage')}</Text>
                                    {
                                        meterDataGrid?.dphvAvgPhsa != null &&
                                        meterDataGrid?.dphvAvgPhsb != null &&
                                        meterDataGrid?.dphvAvgPhsc != null
                                            ? <>
                                                <Text>{formatFixedValue(meterDataGrid.dphvAvgPhsa, 1, 'V')}</Text>
                                                <Text>{formatFixedValue(meterDataGrid.dphvAvgPhsb, 1, 'V')}</Text>
                                                <Text>{formatFixedValue(meterDataGrid.dphvAvgPhsc, 1, 'V')}</Text>
                                            </>
                                            : <>
                                                <Text>-</Text>
                                                <Text>{formatFixedValue(meterDataGrid?.dphvAvg, 1, 'V')}</Text>
                                                <Text>-</Text>
                                            </>
                                    }
                                </div>
                            </div>

                            <div style={styles.marginTop8}>
                                <iframe
                                    src={`https://grafana.telemet.works/d-solo/ef09v1a2fpf5se/efe-hes?orgId=1&from=${Date.now() - 24 * 3600000}&to=${Date.now()}&timezone=browser&var-pod_id=${podId}&theme=${theme}&panelId=7&__feature.dashboardSceneSolo`}
                                    width="450" height="200" frameBorder="2"></iframe>
                            </div>
                            <div style={styles.marginTop8}>
                                <iframe
                                    src={`https://grafana.telemet.works/d-solo/ef09v1a2fpf5se/efe-hes?orgId=1&from=${Date.now() - 24 * 3600000}&to=${Date.now()}&timezone=browser&var-pod_id=${podId}&theme=${theme}&panelId=5&__feature.dashboardSceneSolo`}
                                    width="450" height="200" frameBorder="2"></iframe>
                            </div>
                            <div style={styles.marginTop8}>
                                <iframe
                                    src={`https://grafana.telemet.works/d-solo/ef09v1a2fpf5se/efe-hes?orgId=1&from=${Date.now() - 24 * 3600000}&to=${Date.now()}&timezone=browser&var-pod_id=${podId}&theme=${theme}&panelId=2&__feature.dashboardSceneSolo`}
                                    width="450" height="200" frameBorder="2"></iframe>
                            </div>
                        </div>

                        <div style={{
                            ...styles.row,
                            padding: padding,
                            border: '1px solid lightgray',
                            borderRadius: '8px',
                            width: 'fit-content',
                            height: 'fit-content'
                        }}>
                            <div style={{...styles.justifyContent_center}}>
                                <Text bold>{t('efe.details.manufacturer')}:</Text>
                                <Text bold>{t('efe.details.type')}:</Text>
                                <Text bold>{t('efe.details.family')}:</Text>
                                <Text bold>{t('efe.details.hardwareVersion')}:</Text>
                                <Text bold>{t('efe.details.firmwareVersion')}:</Text>
                            </div>

                            <div style={styles.marginLeft8}>
                                <Text>{meterInverterDataList[0]?.dmaName ?? '-'}</Text>
                                <Text>{meterInverterDataList[0]?.pdvDeviceTemplateTag ?? '-'}</Text>
                                <Text>{meterInverterDataList[0]?.pfaName ?? '-'}</Text>
                                <Text>{meterInverterDataList[0]?.dteHardwareVersion ?? '-'}</Text>
                                <Text>{meterInverterDataList[0]?.pdvFirmwareVersion ?? '-'}</Text>
                            </div>

                            <img
                                src={
                                    meterInverterDataList[0]?.dtePicture
                                        ? `data:image/png;base64,${meterInverterDataList[0].dtePicture}`
                                        : smartMeterImage
                                }
                                alt={t('efe.details.smartMeter')}
                                style={{marginLeft: 16, height: '100px'}}
                            />
                        </div>
                    </div>
                    ) : (
                        <div style={{padding: padding}}>
                            <Text>{t('efe.details.noMeter')}</Text>
                        </div>
                    )}
                    </details>
                </Surface>

                {inverterDevices.map((inverter, index) => {
                    const inverterGrid = inverterDataById[inverter?.pdvDeviceInstanceName ?? ''];

                    return (
                        <Surface
                            key={inverter?.pdvDeviceInstanceName ?? index}
                            style={{
                                ...styles.marginRight8,
                                ...styles.marginLeft8,
                                paddingTop: 8,
                                overflowX: 'auto'
                            }}
                            rounded
                        >
                            <details open={index === 0 || index === inverterDevices.length - 1}>
                                <summary
                                    style={{
                                        ...styles.row,
                                        alignItems: 'center',
                                        padding: padding,
                                        backgroundColor: 'lightblue',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <SolarPowerOutlinedIcon />
                                    <Text bold style={styles.marginLeft8}>
                                        {t('efe.details.inverter')} | {inverter?.pdvDeviceInstanceName ?? '-'}
                                        {' | '}
                                        {t('efe.details.production')}:{' '}
                                        {inverterGrid?.dsupwAvg != null ? `${Math.round(inverterGrid.dsupwAvg)} W` : '-'}
                                        {' | '}
                                        {t('efe.details.inverterPowerLimit')}:{' '}
                                        {inverterGrid?.dwmaxlimpctLast != null ? `${inverterGrid.dwmaxlimpctLast}%` : '-'}
                                    </Text>
                                </summary>

                                <div style={{...styles.row, ...styles.justifyContent_spaceBetween, padding: padding}}>
                                    <div>
                                        <div
                                            style={{
                                                ...styles.row,
                                                padding: padding,
                                                border: '1px solid lightgray',
                                                borderRadius: '8px',
                                                width: 'fit-content'
                                            }}
                                        >
                                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                                <Text bold>{t('efe.details.phase')}</Text>
                                                <Text>PhsA</Text>
                                                <Text>PhsB</Text>
                                                <Text>PhsC</Text>
                                            </div>

                                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', ...styles.marginLeft8}}>
                                                <Text bold>{t('efe.details.production')}</Text>
                                                {inverterGrid?.dsupwAvgPhsa != null &&
                                                inverterGrid?.dsupwAvgPhsb != null &&
                                                inverterGrid?.dsupwAvgPhsc != null ? (
                                                    <>
                                                        <Text>{Math.round(inverterGrid.dsupwAvgPhsa)} W</Text>
                                                        <Text>{Math.round(inverterGrid.dsupwAvgPhsb)} W</Text>
                                                        <Text>{Math.round(inverterGrid.dsupwAvgPhsc)} W</Text>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Text>-</Text>
                                                        <Text>{inverterGrid?.dsupwAvg != null ? `${Math.round(inverterGrid.dsupwAvg)} W` : '-'}</Text>
                                                        <Text>-</Text>
                                                    </>
                                                )}
                                            </div>

                                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', ...styles.marginLeft8}}>
                                                <Text bold>{t('efe.details.voltage')}</Text>
                                                {inverterGrid?.dphvAvgPhsa != null &&
                                                inverterGrid?.dphvAvgPhsb != null &&
                                                inverterGrid?.dphvAvgPhsc != null ? (
                                                    <>
                                                        <Text>{formatFixedValue(inverterGrid.dphvAvgPhsa, 1, 'V')}</Text>
                                                        <Text>{formatFixedValue(inverterGrid.dphvAvgPhsb, 1, 'V')}</Text>
                                                        <Text>{formatFixedValue(inverterGrid.dphvAvgPhsc, 1, 'V')}</Text>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Text>-</Text>
                                                        <Text>{formatFixedValue(inverterGrid?.dphvAvg, 1, 'V')}</Text>
                                                        <Text>-</Text>
                                                    </>
                                                )}
                                            </div>

                                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', ...styles.marginLeft8}}>
                                                <Text bold>{t('efe.details.frequency')}</Text>
                                                {inverterGrid?.dhzAvgPhsa != null &&
                                                inverterGrid?.dhzAvgPhsb != null &&
                                                inverterGrid?.dhzAvgPhsc != null ? (
                                                    <>
                                                        <Text>{formatFixedValue(inverterGrid.dhzAvgPhsa, 2, 'Hz')}</Text>
                                                        <Text>{formatFixedValue(inverterGrid.dhzAvgPhsb, 2, 'Hz')}</Text>
                                                        <Text>{formatFixedValue(inverterGrid.dhzAvgPhsc, 2, 'Hz')}</Text>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Text>-</Text>
                                                        <Text>{formatFixedValue(inverterGrid?.dhzAvg, 2, 'Hz')}</Text>
                                                        <Text>-</Text>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div style={styles.marginTop8}>
                                            <iframe
                                                src={`https://grafana.telemet.works/d-solo/ef09v1a2fpf5se/efe-hes?orgId=1&from=${Date.now() - 24 * 3600000}&to=${Date.now()}&timezone=browser&var-pod_id=${podId}&theme=${theme}&panelId=3&__feature.dashboardSceneSolo`}
                                                width="450"
                                                height="200"
                                                frameBorder="2"
                                            />
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            ...styles.row,
                                            padding: padding,
                                            border: '1px solid lightgray',
                                            borderRadius: '8px',
                                            width: 'fit-content',
                                            height: 'fit-content'
                                        }}
                                    >
                                        <div style={{...styles.justifyContent_center}}>
                                            <Text bold>{t('efe.details.manufacturer')}:</Text>
                                            <Text bold>{t('efe.details.type')}:</Text>
                                            <Text bold>{t('efe.details.family')}:</Text>
                                            <Text bold>{t('efe.details.hardwareVersion')}:</Text>
                                            <Text bold>{t('efe.details.firmwareVersion')}:</Text>
                                            <Text bold>{t('efe.details.numOfPhases')}:</Text>
                                            <Text bold>{t('efe.details.ratedPower')}:</Text>
                                            <Text bold>{t('efe.details.hybrid')}:</Text>
                                            <Text bold>{t('efe.details.phaseSymmetry')}:</Text>
                                        </div>

                                        <div style={styles.marginLeft8}>
                                            <Text>{inverter?.dmaName ?? '-'}</Text>
                                            <Text>{inverter?.dteTemplateName ?? '-'}</Text>
                                            <Text>{inverter?.pfaName ?? '-'}</Text>
                                            <Text>{inverter?.dteHardwareVersion ?? '-'}</Text>
                                            <Text>{inverter?.pdvFirmwareVersion ?? '-'}</Text>
                                            <Text>{inverter?.dte3phaseDevice != null ? (inverter.dte3phaseDevice ? '3' : '-') : '-'}</Text>
                                            <Text>{inverter?.dteInvRatedPower != null ? `${inverter.dteInvRatedPower} W` : '-'}</Text>
                                            <Text>
                                                {inverter?.dteInvHibrid != null
                                                    ? (inverter.dteInvHibrid ? t('efe.details.yes') : t('efe.details.no'))
                                                    : '-'}
                                            </Text>
                                            <Text>
                                                {inverter?.dteInvPhaseSymmetry != null
                                                    ? (inverter.dteInvPhaseSymmetry
                                                        ? t('efe.details.symmetric')
                                                        : `${t('efe.details.no')} ${t('efe.details.symmetric')}`)
                                                    : '-'}
                                            </Text>
                                        </div>

                                        <img
                                            src={
                                                inverter?.dtePicture
                                                    ? `data:image/png;base64,${inverter.dtePicture}`
                                                    : inverterImage
                                            }
                                            alt={t('efe.details.inverter')}
                                            style={{marginLeft: 16, height: '170px'}}
                                        />
                                    </div>
                                </div>

                                        <div style={{
                                            ...styles.row,
                                            alignItems: 'center',
                                            padding: padding,
                                            border: '1px solid lightgray',
                                            borderRadius: '8px',
                                            width: 'fit-content',
                                            ...styles.marginLeft8
                                        }}>
                                            <VerticalAlignTopOutlinedIcon sx={{fontSize: '48px'}}/>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center', ...styles.marginLeft16
                                            }}>
                                                <Text bold style={{fontSize: '110%'}}>{
                                                    isEmpty(podMetrics?.dctrlCmdModeLast)
                                                    ? t('efe.details.noControl')
                                                    : podMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')
                                                        ? t('efe.details.voltageLimit')
                                                        : podMetrics?.dctrlCmdModeLast?.includes('EXPORT')
                                                            ? t('efe.details.exportPowerLimit')
                                                            : t('efe.details.inverterPowerLimit') + (podMetrics?.dctrlCmdModeLast?.includes('PERCENT') ? ' (%)' : ' (Watt)')}</Text>
                                                <Text bold style={{fontSize: '170%'}}>{inverterGrid?.dwmaxlimpctLast != null ? `${inverterGrid.dwmaxlimpctLast}%` : '-'}</Text>
                                                <Text>Export limit:
                                                    {controlData?.cocControlParam != null
                                                        ? podMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')
                                                            ? `${controlData.cocControlParam} V`
                                                            : podMetrics?.dctrlCmdModeLast?.includes('PERCENT')
                                                                ? `${controlData.cocControlParam} %`
                                                                : `${controlData.cocControlParam} W`
                                                        : '-'}</Text>
                                            </div>
                                        </div>
                            </details>
                        </Surface>
                    );
                })}

                {/*<Surface style={{*/}
                {/*    ...styles.marginRight8, ...styles.marginLeft8, ...{*/}
                {/*        paddingTop: 8,*/}
                {/*        overflowX: 'auto'*/}
                {/*    }*/}
                {/*}} rounded>*/}
                {/*    <details open>*/}
                {/*        <summary style={{...styles.row, alignItems: 'center', padding: padding, backgroundColor: 'lightblue', cursor: 'pointer'}}>*/}
                {/*            <SolarPowerOutlinedIcon/>*/}
                {/*            <Text bold style={styles.marginLeft8}>*/}
                {/*                {t('efe.details.inverter')} | {meterInverterDataList[inverterIdx]?.pdvDeviceInstanceName ?? '-'}*/}
                {/*                {' | '}*/}
                {/*                {t('efe.details.production')}: {inverterDataGrid?.dsupwAvg != null ? `${Math.round(inverterDataGrid.dsupwAvg)} W` : '-'}*/}
                {/*                {' | '}*/}
                {/*                {t('efe.details.inverterPowerLimit')}: {inverterDataGrid?.dwmaxlimpctLast != null ? `${inverterDataGrid.dwmaxlimpctLast}%` : '-'}*/}
                {/*            </Text>*/}
                {/*        </summary>*/}
                {/*    <div style={{...styles.row, ...styles.justifyContent_spaceBetween, padding: padding}}>*/}
                {/*        <div>*/}
                {/*            <div style={{*/}
                {/*                ...styles.row,*/}
                {/*                padding: padding,*/}
                {/*                border: '1px solid lightgray',*/}
                {/*                borderRadius: '8px',*/}
                {/*                width: 'fit-content'*/}
                {/*            }}>*/}
                {/*                <div style={{*/}
                {/*                    display: 'flex',*/}
                {/*                    flexDirection: 'column',*/}
                {/*                    alignItems: 'center'*/}
                {/*                }}>*/}
                {/*                    <Text bold>{t('efe.details.phase')}</Text>*/}
                {/*                    <Text>PhsA</Text>*/}
                {/*                    <Text>PhsB</Text>*/}
                {/*                    <Text>PhsC</Text>*/}
                {/*                </div>*/}

                {/*                /!* Termelés (supw) *!/*/}
                {/*                <div style={{*/}
                {/*                    display: 'flex',*/}
                {/*                    flexDirection: 'column',*/}
                {/*                    alignItems: 'center',*/}
                {/*                    ...styles.marginLeft8*/}
                {/*                }}>*/}
                {/*                    <Text bold>{t('efe.details.production')}</Text>*/}

                {/*                    {*/}
                {/*                        inverterDataGrid?.dsupwAvgPhsa != null &&*/}
                {/*                        inverterDataGrid?.dsupwAvgPhsb != null &&*/}
                {/*                        inverterDataGrid?.dsupwAvgPhsc != null*/}
                {/*                            ? (*/}
                {/*                                <>*/}
                {/*                                    <Text>{Math.round(inverterDataGrid.dsupwAvgPhsa)} W</Text>*/}
                {/*                                    <Text>{Math.round(inverterDataGrid.dsupwAvgPhsb)} W</Text>*/}
                {/*                                    <Text>{Math.round(inverterDataGrid.dsupwAvgPhsc)} W</Text>*/}
                {/*                                </>*/}
                {/*                            )*/}
                {/*                            : (*/}
                {/*                                <>*/}
                {/*                                    <Text>-</Text>*/}
                {/*                                    <Text>{inverterDataGrid?.dsupwAvg != null ? Math.round(inverterDataGrid.dsupwAvg) + ' W' : '-'}</Text>*/}
                {/*                                    <Text>-</Text>*/}
                {/*                                </>*/}
                {/*                            )*/}
                {/*                    }*/}
                {/*                </div>*/}

                {/*                /!* Feszültség (phv) *!/*/}
                {/*                <div style={{*/}
                {/*                    display: 'flex',*/}
                {/*                    flexDirection: 'column',*/}
                {/*                    alignItems: 'center',*/}
                {/*                    ...styles.marginLeft8*/}
                {/*                }}>*/}
                {/*                    <Text bold>{t('efe.details.voltage')}</Text>*/}

                {/*                    {*/}
                {/*                        inverterDataGrid?.dphvAvgPhsa != null &&*/}
                {/*                        inverterDataGrid?.dphvAvgPhsb != null &&*/}
                {/*                        inverterDataGrid?.dphvAvgPhsc != null*/}
                {/*                            ? (*/}
                {/*                                <>*/}
                {/*                                    <Text>{inverterDataGrid.dphvAvgPhsa.toFixed(1)} V</Text>*/}
                {/*                                    <Text>{inverterDataGrid.dphvAvgPhsb.toFixed(1)} V</Text>*/}
                {/*                                    <Text>{inverterDataGrid.dphvAvgPhsc.toFixed(1)} V</Text>*/}
                {/*                                </>*/}
                {/*                            )*/}
                {/*                            : (*/}
                {/*                                <>*/}
                {/*                                    <Text>-</Text>*/}
                {/*                                    <Text>{inverterDataGrid?.dphvAvg != null ? inverterDataGrid.dphvAvg.toFixed(1) + ' V' : '-'}</Text>*/}
                {/*                                    <Text>-</Text>*/}
                {/*                                </>*/}
                {/*                            )*/}
                {/*                    }*/}
                {/*                </div>*/}

                {/*                /!* Frekvencia (hz) *!/*/}
                {/*                <div style={{*/}
                {/*                    display: 'flex',*/}
                {/*                    flexDirection: 'column',*/}
                {/*                    alignItems: 'center',*/}
                {/*                    ...styles.marginLeft8*/}
                {/*                }}>*/}
                {/*                    <Text bold>{t('efe.details.frequency')}</Text>*/}

                {/*                    {*/}
                {/*                        inverterDataGrid?.dhzAvgPhsa != null &&*/}
                {/*                        inverterDataGrid?.dhzAvgPhsb != null &&*/}
                {/*                        inverterDataGrid?.dhzAvgPhsc != null*/}
                {/*                            ? (*/}
                {/*                                <>*/}
                {/*                                    <Text>{inverterDataGrid.dhzAvgPhsa.toFixed(2)} Hz</Text>*/}
                {/*                                    <Text>{inverterDataGrid.dhzAvgPhsb.toFixed(2)} Hz</Text>*/}
                {/*                                    <Text>{inverterDataGrid.dhzAvgPhsc.toFixed(2)} Hz</Text>*/}
                {/*                                </>*/}
                {/*                            )*/}
                {/*                            : (*/}
                {/*                                <>*/}
                {/*                                    <Text>-</Text>*/}
                {/*                                    <Text>{inverterDataGrid?.dhzAvg != null ? inverterDataGrid.dhzAvg.toFixed(2) + ' Hz' : '-'}</Text>*/}
                {/*                                    <Text>-</Text>*/}
                {/*                                </>*/}
                {/*                            )*/}
                {/*                    }*/}
                {/*                </div>*/}
                {/*            </div>*/}

                {/*                <div style={styles.marginTop8}>*/}
                {/*                    <iframe*/}
                {/*                        src={`https://grafana.telemet.works/d-solo/ef09v1a2fpf5se/efe-hes?orgId=1&from=${Date.now() - 24 * 3600000}&to=${Date.now()}&timezone=browser&var-pod_id=${podId}&theme=${theme}&panelId=3&__feature.dashboardSceneSolo`}*/}
                {/*                        width="450" height="200" frameBorder="2"></iframe>*/}
                {/*                </div>*/}
                {/*            </div>*/}

                {/*        <div style={{*/}
                {/*            ...styles.row,*/}
                {/*            padding: padding,*/}
                {/*            border: '1px solid lightgray',*/}
                {/*            borderRadius: '8px',*/}
                {/*            width: 'fit-content',*/}
                {/*            height: 'fit-content'*/}
                {/*        }}>*/}
                {/*            <div style={{...styles.justifyContent_center}}>*/}
                {/*                <Text bold>{t('efe.details.manufacturer')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.type')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.family')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.hardwareVersion')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.firmwareVersion')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.numOfPhases')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.ratedPower')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.hybrid')}:</Text>*/}
                {/*                <Text bold>{t('efe.details.phaseSymmetry')}:</Text>*/}
                {/*            </div>*/}

                {/*            <div style={styles.marginLeft8}>*/}
                {/*                <Text>{meterInverterDataList[inverterIdx]?.dmaName ?? '-'}</Text>*/}
                {/*                <Text>{meterInverterDataList[inverterIdx]?.pdvDeviceTemplateTag ?? '-'}</Text>*/}
                {/*                <Text>{meterInverterDataList[inverterIdx]?.pfaName ?? '-'}</Text>*/}
                {/*                <Text>{meterInverterDataList[inverterIdx]?.dteHardwareVersion ?? '-'}</Text>*/}
                {/*                <Text>{meterInverterDataList[inverterIdx]?.pdvFirmwareVersion ?? '-'}</Text>*/}
                {/*                <Text>{meterInverterDataList[inverterIdx]?.dte3phaseDevice != null ? (meterInverterDataList[inverterIdx].dte3phaseDevice ? '3' : '-') : '-'}</Text>*/}
                {/*                <Text>{ratedPower?.dwmaxOnce != null ? ratedPower.dwmaxOnce + 'W' : '-'}</Text>*/}
                {/*                <Text>*/}
                {/*                    {meterInverterDataList[inverterIdx]?.dteInvHibrid != null*/}
                {/*                        ? (meterInverterDataList[inverterIdx].dteInvHibrid ? t('efe.details.yes') : t('efe.details.no'))*/}
                {/*                        : '-'}*/}
                {/*                </Text>*/}
                {/*                <Text>*/}
                {/*                    {meterInverterDataList[inverterIdx]?.dteInvPhaseSymmetry != null*/}
                {/*                        ? (meterInverterDataList[inverterIdx].dteInvPhaseSymmetry*/}
                {/*                            ? t('efe.details.symmetric')*/}
                {/*                            : t('efe.details.no') + ' ' + t('efe.details.symmetric'))*/}
                {/*                        : '-'}*/}
                {/*                </Text>*/}
                {/*            </div>*/}

                {/*            <img*/}
                {/*                src={*/}
                {/*                    meterInverterDataList[inverterIdx]?.dtePicture*/}
                {/*                        ? `data:image/png;base64,${meterInverterDataList[inverterIdx].dtePicture}`*/}
                {/*                        : inverterImage*/}
                {/*                }*/}
                {/*                alt={t('efe.details.inverter')}*/}
                {/*                style={{marginLeft: 16, height: '170px'}}*/}
                {/*            />*/}
                {/*        </div>*/}
                {/*    </div>*/}

                {/*        <div style={{*/}
                {/*            ...styles.row,*/}
                {/*            alignItems: 'center',*/}
                {/*            padding: padding,*/}
                {/*            border: '1px solid lightgray',*/}
                {/*            borderRadius: '8px',*/}
                {/*            width: 'fit-content',*/}
                {/*            ...styles.marginLeft8*/}
                {/*        }}>*/}
                {/*            <VerticalAlignTopOutlinedIcon sx={{fontSize: '48px'}}/>*/}
                {/*            <div style={{*/}
                {/*                display: 'flex',*/}
                {/*                flexDirection: 'column',*/}
                {/*                alignItems: 'center', ...styles.marginLeft16*/}
                {/*            }}>*/}
                {/*                <Text bold style={{fontSize: '110%'}}>{*/}
                {/*                    isEmpty(efeMetrics?.dctrlCmdModeLast)*/}
                {/*                    ? t('efe.details.noControl')*/}
                {/*                    : efeMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')*/}
                {/*                        ? t('efe.details.voltageLimit')*/}
                {/*                        : efeMetrics?.dctrlCmdModeLast?.includes('EXPORT')*/}
                {/*                            ? t('efe.details.exportPowerLimit')*/}
                {/*                            : t('efe.details.inverterPowerLimit') + (efeMetrics?.dctrlCmdModeLast?.includes('PERCENT') ? ' (%)' : ' (Watt)')}</Text>*/}
                {/*                <Text bold style={{fontSize: '170%'}}>{inverterDataGrid?.dwmaxlimpctLast != null ? `${inverterDataGrid.dwmaxlimpctLast}%` : '-'}</Text>*/}
                {/*                <Text>Export limit:*/}
                {/*                    {controlData?.cocControlParam != null*/}
                {/*                        ? efeMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')*/}
                {/*                            ? `${controlData.cocControlParam} V`*/}
                {/*                            : efeMetrics?.dctrlCmdModeLast?.includes('PERCENT')*/}
                {/*                                ? `${controlData.cocControlParam} %`*/}
                {/*                                : `${controlData.cocControlParam} W`*/}
                {/*                        : '-'}</Text>*/}
                {/*            </div>*/}
                {/*        </div>*/}

                {/*    </details>*/}
                {/*</Surface>*/}

                {/*{meterInverterDataList[inverterIdx + 1] && (*/}
                {/*    <Surface style={{*/}
                {/*        ...styles.marginRight8, ...styles.marginLeft8, ...{*/}
                {/*            paddingTop: 8,*/}
                {/*            overflowX: 'auto'*/}
                {/*        }*/}
                {/*    }} rounded>*/}
                {/*        <details open>*/}
                {/*            <summary style={{...styles.row, alignItems: 'center', padding: padding, backgroundColor: 'lightblue', cursor: 'pointer'}}>*/}
                {/*                <SolarPowerOutlinedIcon/>*/}
                {/*                <Text bold style={styles.marginLeft8}>*/}
                {/*                    {t('efe.details.inverter')} | {meterInverterDataList[inverterIdx + 1]?.pdvDeviceInstanceName ?? '-'}*/}
                {/*                    {' | '}*/}
                {/*                    {t('efe.details.production')}: {inverterDataGridEmul?.dsupwAvg != null ? `${Math.round(inverterDataGridEmul.dsupwAvg)} W` : '-'}*/}
                {/*                    {' | '}*/}
                {/*                    {t('efe.details.inverterPowerLimit')}: {inverterDataGridEmul?.dwmaxlimpctLast != null ? `${inverterDataGridEmul.dwmaxlimpctLast}%` : '-'}*/}
                {/*                </Text>*/}
                {/*            </summary>*/}
                {/*        <div style={{...styles.row, ...styles.justifyContent_spaceBetween, padding: padding}}>*/}
                {/*            <div>*/}
                {/*                <div style={{*/}
                {/*                    ...styles.row,*/}
                {/*                    padding: padding,*/}
                {/*                    border: '1px solid lightgray',*/}
                {/*                    borderRadius: '8px',*/}
                {/*                    width: 'fit-content'*/}
                {/*                }}>*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        flexDirection: 'column',*/}
                {/*                        alignItems: 'center'*/}
                {/*                    }}>*/}
                {/*                        <Text bold>{t('efe.details.phase')}</Text>*/}
                {/*                        <Text>PhsA</Text>*/}
                {/*                        <Text>PhsB</Text>*/}
                {/*                        <Text>PhsC</Text>*/}
                {/*                    </div>*/}

                {/*                    /!* Termelés (supw) *!/*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        flexDirection: 'column',*/}
                {/*                        alignItems: 'center',*/}
                {/*                        ...styles.marginLeft8*/}
                {/*                    }}>*/}
                {/*                        <Text bold>{t('efe.details.production')}</Text>*/}

                {/*                        {*/}
                {/*                            inverterDataGridEmul?.dsupwAvgPhsa != null &&*/}
                {/*                            inverterDataGridEmul?.dsupwAvgPhsb != null &&*/}
                {/*                            inverterDataGridEmul?.dsupwAvgPhsc != null*/}
                {/*                                ? (*/}
                {/*                                    <>*/}
                {/*                                        <Text>{Math.round(inverterDataGridEmul.dsupwAvgPhsa)} W</Text>*/}
                {/*                                        <Text>{Math.round(inverterDataGridEmul.dsupwAvgPhsb)} W</Text>*/}
                {/*                                        <Text>{Math.round(inverterDataGridEmul.dsupwAvgPhsc)} W</Text>*/}
                {/*                                    </>*/}
                {/*                                )*/}
                {/*                                : (*/}
                {/*                                    <>*/}
                {/*                                        <Text>-</Text>*/}
                {/*                                        <Text>{inverterDataGridEmul?.dsupwAvg != null ? Math.round(inverterDataGridEmul.dsupwAvg) + ' W' : '-'}</Text>*/}
                {/*                                        <Text>-</Text>*/}
                {/*                                    </>*/}
                {/*                                )*/}
                {/*                        }*/}
                {/*                    </div>*/}

                {/*                    /!* Feszültség (phv) *!/*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        flexDirection: 'column',*/}
                {/*                        alignItems: 'center',*/}
                {/*                        ...styles.marginLeft8*/}
                {/*                    }}>*/}
                {/*                        <Text bold>{t('efe.details.voltage')}</Text>*/}

                {/*                        {*/}
                {/*                            inverterDataGridEmul?.dphvAvgPhsa != null &&*/}
                {/*                            inverterDataGridEmul?.dphvAvgPhsb != null &&*/}
                {/*                            inverterDataGridEmul?.dphvAvgPhsc != null*/}
                {/*                                ? (*/}
                {/*                                    <>*/}
                {/*                                        <Text>{inverterDataGridEmul.dphvAvgPhsa.toFixed(1)} V</Text>*/}
                {/*                                        <Text>{inverterDataGridEmul.dphvAvgPhsb.toFixed(1)} V</Text>*/}
                {/*                                        <Text>{inverterDataGridEmul.dphvAvgPhsc.toFixed(1)} V</Text>*/}
                {/*                                    </>*/}
                {/*                                )*/}
                {/*                                : (*/}
                {/*                                    <>*/}
                {/*                                        <Text>-</Text>*/}
                {/*                                        <Text>{inverterDataGridEmul?.dphvAvg != null ? inverterDataGridEmul.dphvAvg.toFixed(1) + ' V' : '-'}</Text>*/}
                {/*                                        <Text>-</Text>*/}
                {/*                                    </>*/}
                {/*                                )*/}
                {/*                        }*/}
                {/*                    </div>*/}

                {/*                    /!* Frekvencia (hz) *!/*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        flexDirection: 'column',*/}
                {/*                        alignItems: 'center',*/}
                {/*                        ...styles.marginLeft8*/}
                {/*                    }}>*/}
                {/*                        <Text bold>{t('efe.details.frequency')}</Text>*/}

                {/*                        {*/}
                {/*                            inverterDataGridEmul?.dhzAvgPhsa != null &&*/}
                {/*                            inverterDataGridEmul?.dhzAvgPhsb != null &&*/}
                {/*                            inverterDataGridEmul?.dhzAvgPhsc != null*/}
                {/*                                ? (*/}
                {/*                                    <>*/}
                {/*                                        <Text>{inverterDataGridEmul.dhzAvgPhsa.toFixed(2)} Hz</Text>*/}
                {/*                                        <Text>{inverterDataGridEmul.dhzAvgPhsb.toFixed(2)} Hz</Text>*/}
                {/*                                        <Text>{inverterDataGridEmul.dhzAvgPhsc.toFixed(2)} Hz</Text>*/}
                {/*                                    </>*/}
                {/*                                )*/}
                {/*                                : (*/}
                {/*                                    <>*/}
                {/*                                        <Text>-</Text>*/}
                {/*                                        <Text>{inverterDataGridEmul?.dhzAvg != null ? inverterDataGridEmul.dhzAvg.toFixed(2) + ' Hz' : '-'}</Text>*/}
                {/*                                        <Text>-</Text>*/}
                {/*                                    </>*/}
                {/*                                )*/}
                {/*                        }*/}
                {/*                    </div>*/}
                {/*                </div>*/}

                {/*                    <div style={styles.marginTop8}>*/}
                {/*                        <iframe*/}
                {/*                            src={`https://grafana.telemet.works/d-solo/ef09v1a2fpf5se/efe-hes?orgId=1&from=${Date.now() - 24 * 3600000}&to=${Date.now()}&timezone=browser&var-pod_id=${podId}&theme=${theme}&panelId=3&__feature.dashboardSceneSolo`}*/}
                {/*                            width="450" height="200" frameBorder="2"></iframe>*/}
                {/*                    </div>*/}
                {/*                </div>*/}

                {/*            <div style={{*/}
                {/*                ...styles.row,*/}
                {/*                padding: padding,*/}
                {/*                border: '1px solid lightgray',*/}
                {/*                borderRadius: '8px',*/}
                {/*                width: 'fit-content',*/}
                {/*                height: 'fit-content'*/}
                {/*            }}>*/}
                {/*                <div style={{...styles.justifyContent_center}}>*/}
                {/*                    <Text bold>{t('efe.details.manufacturer')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.type')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.family')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.hardwareVersion')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.firmwareVersion')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.numOfPhases')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.ratedPower')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.hybrid')}:</Text>*/}
                {/*                    <Text bold>{t('efe.details.phaseSymmetry')}:</Text>*/}
                {/*                </div>*/}

                {/*                <div style={styles.marginLeft8}>*/}
                {/*                    <Text>{meterInverterDataList[inverterIdx + 1]?.dmaName ?? '-'}</Text>*/}
                {/*                    <Text>{meterInverterDataList[inverterIdx + 1]?.pdvDeviceTemplateTag ?? '-'}</Text>*/}
                {/*                    <Text>{meterInverterDataList[inverterIdx + 1]?.pfaName ?? '-'}</Text>*/}
                {/*                    <Text>{meterInverterDataList[inverterIdx + 1]?.dteHardwareVersion ?? '-'}</Text>*/}
                {/*                    <Text>{meterInverterDataList[inverterIdx + 1]?.pdvFirmwareVersion ?? '-'}</Text>*/}
                {/*                    <Text>{meterInverterDataList[inverterIdx + 1]?.dte3phaseDevice != null ? (meterInverterDataList[inverterIdx + 1].dte3phaseDevice ? '3' : '-') : '-'}</Text>*/}
                {/*                    <Text>{ratedPower?.dwmaxOnce != null ? ratedPower.dwmaxOnce + 'W' : '-'}</Text>*/}
                {/*                    <Text>*/}
                {/*                        {meterInverterDataList[inverterIdx + 1]?.dteInvHibrid != null*/}
                {/*                            ? (meterInverterDataList[inverterIdx + 1].dteInvHibrid ? t('efe.details.yes') : t('efe.details.no'))*/}
                {/*                            : '-'}*/}
                {/*                    </Text>*/}
                {/*                    <Text>*/}
                {/*                        {meterInverterDataList[inverterIdx + 1]?.dteInvPhaseSymmetry != null*/}
                {/*                            ? (meterInverterDataList[inverterIdx + 1].dteInvPhaseSymmetry*/}
                {/*                                ? t('efe.details.symmetric')*/}
                {/*                                : t('efe.details.no') + ' ' + t('efe.details.symmetric'))*/}
                {/*                            : '-'}*/}
                {/*                    </Text>*/}
                {/*                </div>*/}

                {/*                <img*/}
                {/*                    src={inverterImage}*/}
                {/*                    alt={t('efe.details.inverter')}*/}
                {/*                    style={{marginLeft: 16, height: '170px'}}*/}
                {/*                />*/}
                {/*            </div>*/}
                {/*        </div>*/}

                {/*            <div style={{*/}
                {/*                ...styles.row,*/}
                {/*                alignItems: 'center',*/}
                {/*                padding: padding,*/}
                {/*                border: '1px solid lightgray',*/}
                {/*                borderRadius: '8px',*/}
                {/*                width: 'fit-content',*/}
                {/*                ...styles.marginLeft8*/}
                {/*            }}>*/}
                {/*            <VerticalAlignTopOutlinedIcon sx={{fontSize: '48px'}}/>*/}
                {/*                <div style={{*/}
                {/*                    display: 'flex',*/}
                {/*                    flexDirection: 'column',*/}
                {/*                    alignItems: 'center', ...styles.marginLeft16*/}
                {/*                }}>*/}
                {/*                    <Text bold style={{fontSize: '110%'}}>{*/}
                {/*                        isEmpty(efeMetrics?.dctrlCmdModeLast)*/}
                {/*                        ? t('efe.details.noControl')*/}
                {/*                        : efeMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')*/}
                {/*                            ? t('efe.details.voltageLimit')*/}
                {/*                            : efeMetrics?.dctrlCmdModeLast?.includes('EXPORT')*/}
                {/*                                ? t('efe.details.exportPowerLimit')*/}
                {/*                                : t('efe.details.inverterPowerLimit') + (efeMetrics?.dctrlCmdModeLast?.includes('PERCENT') ? ' (%)' : ' (Watt)')}</Text>*/}
                {/*                    <Text bold style={{fontSize: '170%'}}>{inverterDataGridEmul?.dwmaxlimpctLast != null ? `${inverterDataGridEmul.dwmaxlimpctLast}%` : '-'}</Text>*/}
                {/*                    <Text>Export limit:*/}
                {/*                        {controlData?.cocControlParam != null*/}
                {/*                            ? efeMetrics?.dctrlCmdModeLast?.includes('VOLTAGE')*/}
                {/*                                ? `${controlData.cocControlParam} V`*/}
                {/*                                : efeMetrics?.dctrlCmdModeLast?.includes('PERCENT')*/}
                {/*                                    ? `${controlData.cocControlParam} %`*/}
                {/*                                    : `${controlData.cocControlParam} W`*/}
                {/*                            : '-'}</Text>*/}
                {/*                </div>*/}
                {/*            </div>*/}

                {/*        </details>*/}
                {/*    </Surface>*/}
                {/*)}*/}

                <Surface style={{
                    ...styles.marginRight8, ...styles.marginLeft8, ...{
                        paddingTop: 8,
                        overflowX: 'auto'
                    }
                }} rounded>
                    <details>
                        <summary style={{...styles.row, alignItems: 'center', padding: padding, backgroundColor: 'lightblue', cursor: 'pointer'}}>
                            <HeatPumpOutlinedIcon/>
                            <Text bold style={styles.marginLeft8}>
                                {`${t('efe.details.heatPump')} | ${t('efe.details.noPump')}`}
                            </Text>
                        </summary>

                        <div style={{padding: padding}}>
                            <Text>{t('efe.details.noPump')}</Text>
                        </div>

                    {/*<div style={{*/}
                    {/*    ...styles.row,*/}
                    {/*    padding: padding, ...styles.justifyContent_spaceBetween*/}
                    {/*}}>*/}
                    {/*    <div style={{*/}
                    {/*        ...styles.row,*/}
                    {/*        padding: padding,*/}
                    {/*        border: '1px solid lightgray',*/}
                    {/*        borderRadius: '8px'*/}
                    {/*    }}>*/}
                    {/*        <div style={{*/}
                    {/*            display: 'flex',*/}
                    {/*            flexDirection: 'column',*/}
                    {/*            alignItems: 'center'*/}
                    {/*        }}>*/}
                    {/*            <Text bold>header1</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*        </div>*/}
                    {/*        <div style={{*/}
                    {/*            display: 'flex',*/}
                    {/*            flexDirection: 'column',*/}
                    {/*            alignItems: 'center', ...styles.marginLeft8*/}
                    {/*        }}>*/}
                    {/*            <Text bold>header2</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*        </div>*/}
                    {/*        <div style={{*/}
                    {/*            display: 'flex',*/}
                    {/*            flexDirection: 'column',*/}
                    {/*            alignItems: 'center', ...styles.marginLeft8*/}
                    {/*        }}>*/}
                    {/*            <Text bold>header3</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*        </div>*/}
                    {/*        <div style={{*/}
                    {/*            display: 'flex',*/}
                    {/*            flexDirection: 'column',*/}
                    {/*            alignItems: 'center', ...styles.marginLeft8*/}
                    {/*        }}>*/}
                    {/*            <Text bold>header4</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*        </div>*/}
                    {/*        <div style={{*/}
                    {/*            display: 'flex',*/}
                    {/*            flexDirection: 'column',*/}
                    {/*            alignItems: 'center', ...styles.marginLeft8*/}
                    {/*        }}>*/}
                    {/*            <Text bold>header5</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*            <Text>data</Text>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}

                    {/*    <div style={{*/}
                    {/*        ...styles.row,*/}
                    {/*        padding: padding,*/}
                    {/*        border: '1px solid lightgray',*/}
                    {/*        borderRadius: '8px'*/}
                    {/*    }}>*/}
                    {/*        <div style={{...styles.justifyContent_center}}>*/}
                    {/*            <Text bold>{t('efe.details.manufacturer')}:</Text>*/}
                    {/*            <Text bold>{t('efe.details.type')}:</Text>*/}
                    {/*            <Text bold>{t('efe.details.family')}:</Text>*/}
                    {/*            <Text bold>{t('efe.details.hardwareVersion')}:</Text>*/}
                    {/*            <Text bold>{t('efe.details.firmwareVersion')}:</Text>*/}
                    {/*        </div>*/}

                    {/*        <div style={styles.marginLeft8}>*/}
                    {/*            <Text>LG</Text>*/}
                    {/*            <Text>Therma V Monobloc R290</Text>*/}
                    {/*            <Text>Therma V</Text>*/}
                    {/*            <Text>v1.0</Text>*/}
                    {/*            <Text>1.2.3</Text>*/}
                    {/*        </div>*/}

                    {/*        <img*/}
                    {/*            src={heatpumpImage}*/}
                    {/*            alt={t('efe.details.heatPump')}*/}
                    {/*            style={{marginLeft: 16, height: '100px', alignItems: 'center'}}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    </details>
                </Surface>

                <Surface style={{
                    ...styles.marginRight8, ...styles.marginLeft8, ...{
                        paddingTop: 8,
                        overflowX: 'auto'
                    }
                }} rounded>
                    <details open>
                        <summary style={{...styles.row, alignItems: 'center', padding: padding, backgroundColor: 'lightblue', cursor: 'pointer'}}>
                            <EventOutlinedIcon/>
                            <Text bold style={styles.marginLeft8}>{t('efe.details.eventLog')}</Text>
                        </summary>
                        <div style={{...styles.marginTop8}}>
                            <Table
                                id="EFE_DETAILS_EVENTS_TABLE"
                                columns={columns}
                                data={podEventsList}
                                loading={isLoading}
                                refreshData={() => dispatch(getEfeEvents(id))}
                                dense
                                useGlobalFilter
                                useColumnVisibility
                                labels={{
                                    add: t('add'),
                                    deletePreferences: t('deletePreferences'),
                                    jumpHere: t('jumpHere'),
                                    page: t('page'),
                                    refresh: t('refresh'),
                                    row: t('row'),
                                    rowsSelected: t('rowsSelected'),
                                    savePreferencies: t('savePreferences'),
                                    searchData: t('searchData'),
                                    selectCurrentPageData: t('selectCurrentPageData'),
                                    showAll: t('showAll'),
                                    sizing: t('sizing')
                                }}
                            />
                        </div>
                    </details>
                </Surface>
            </Grid.Container>
        </Surface>
    )
}
export default MeasurementLocationDetails;
